// hooks/usePolyShiftGroups.js
import { useState, useCallback, useMemo } from "react";

const createEmptyTimepoint = (index) => ({
  groupName: `Fraction ${index + 1}`,
  controlFiles: [],
  treatFiles: [],
});

export const usePolyShiftGroups = (allFiles = []) => {
  // Makes first 2 fractions visible by default
  const [timepoints, setTimepoints] = useState([createEmptyTimepoint(0),
                                                createEmptyTimepoint(1)]);
  const [currentMaxVisibleTimepoint, setCurrentMaxVisibleTimepoint] =
    useState(1);

  const addTimepoint = useCallback(() => {
    if (currentMaxVisibleTimepoint < 12) {
      const newIndex = currentMaxVisibleTimepoint + 1;
      setTimepoints((prev) => {
        const newTimepoints = [...prev];
        if (newTimepoints.length <= newIndex) {
          newTimepoints.push(createEmptyTimepoint(newIndex));
        }
        return newTimepoints;
      });
      setCurrentMaxVisibleTimepoint(newIndex);
    }
  }, [currentMaxVisibleTimepoint]);

  const removeTimepoint = useCallback(() => {
    if (currentMaxVisibleTimepoint > 0) {
      setCurrentMaxVisibleTimepoint((prev) => prev - 1);
    }
  }, [currentMaxVisibleTimepoint]);

  const updateTimepointName = useCallback((index, name) => {
    setTimepoints((prev) =>
      prev.map((tp, i) => (i === index ? { ...tp, groupName: name } : tp))
    );
  }, []);

  const addFilesToTimepoint = useCallback(
    (timepointIndex, condType, fileIds) => {
      const normalizedFileIds = Array.isArray(fileIds) ? fileIds : [fileIds];

      if (normalizedFileIds.length === 0) return;

      setTimepoints((prev) =>
        prev.map((tp, i) => {
          if (i !== timepointIndex) {
            return {
              ...tp,
              controlFiles: tp.controlFiles.filter(
                (id) => !normalizedFileIds.includes(id)
              ),
              treatFiles: tp.treatFiles.filter(
                (id) => !normalizedFileIds.includes(id)
              ),
            };
          }

          const updatedTp = { ...tp };
          if (condType === "control") {
            updatedTp.treatFiles = updatedTp.treatFiles.filter(
              (id) => !normalizedFileIds.includes(id)
            );
            updatedTp.controlFiles = [
              ...new Set([...updatedTp.controlFiles, ...normalizedFileIds]),
            ];
          } else if (condType === "treat") {
            updatedTp.controlFiles = updatedTp.controlFiles.filter(
              (id) => !normalizedFileIds.includes(id)
            );
            updatedTp.treatFiles = [
              ...new Set([...updatedTp.treatFiles, ...normalizedFileIds]),
            ];
          }

          return updatedTp;
        })
      );
    },
    []
  );

  const removeFilesFromTimepoint = useCallback(
    (timepointIndex, condType, fileIds) => {
      const normalizedFileIds = Array.isArray(fileIds) ? fileIds : [fileIds];

      if (normalizedFileIds.length === 0) return;

      setTimepoints((prev) =>
        prev.map((tp, i) => {
          if (i === timepointIndex) {
            const updatedTp = { ...tp };
            if (condType === "control") {
              updatedTp.controlFiles = updatedTp.controlFiles.filter(
                (id) => !normalizedFileIds.includes(id)
              );
            } else if (condType === "treat") {
              updatedTp.treatFiles = updatedTp.treatFiles.filter(
                (id) => !normalizedFileIds.includes(id)
              );
            }
            return updatedTp;
          }
          return tp;
        })
      );
    },
    []
  );

  const clearTimepoint = useCallback((index) => {
    setTimepoints((prev) =>
      prev.map((tp, i) =>
        i === index
          ? {
              ...tp,
              controlFiles: [],
              treatFiles: [],
            }
          : tp
      )
    );
  }, []);

  const clearAllTimepoints = useCallback(() => {
    setTimepoints((prev) =>
      prev.map((tp) => ({
        ...tp,
        controlFiles: [],
        treatFiles: [],
      }))
    );
  }, []);

  const validateTimepoints = useCallback(() => {
    const errors = [];
    const activeTimepoints = timepoints.slice(
      0,
      currentMaxVisibleTimepoint + 1
    );

    const hasAnyFiles = activeTimepoints.some(
      (tp) => tp.controlFiles.length > 0 || tp.treatFiles.length > 0
    );

    if (!hasAnyFiles) {
      errors.push("Please assign at least one file to a fraction");
      return { isValid: false, errors };
    }

    const fractionsWithFiles = activeTimepoints.filter(
      (tp) => tp.controlFiles.length > 0 || tp.treatFiles.length > 0
    );

    if (fractionsWithFiles.length < 1) {
      errors.push("Please assign files to at least 1 fractions for comparison");
    }

    const hasControlFiles = activeTimepoints.some(
      (tp) => tp.controlFiles.length > 0
    );
    const hasTreatFiles = activeTimepoints.some(
      (tp) => tp.treatFiles.length > 0
    );

    if (!hasControlFiles || !hasTreatFiles) {
      errors.push("Please assign files to both control and treatment groups");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [timepoints, currentMaxVisibleTimepoint]);

  const getMasterFileDict = useCallback(() => {
    const masterDict = {};

    // Helper function to get file names by IDs
    const getFileNames = (fileIds) => {
      return fileIds.map((id) => {
        const file = allFiles.find((f) => f.file_id === id);
        return file ? file.file_name : `file_${id}`;
      });
    };

    // Always create all timepoints (time_0 through time_12) just like TimeSeries
    for (let index = 0; index <= 12; index++) {
      const timepointKey = `time_${index}`;
      const timepoint = timepoints[index];

      if (timepoint && index <= currentMaxVisibleTimepoint) {
        // Use actual timepoint data if it exists and is visible
        // Put actual data in the old field names, keep RNA fields empty
        masterDict[timepointKey] = {
          control_file_ids: timepoint.controlFiles.map(String),
          control_file_names: getFileNames(timepoint.controlFiles),
          treat_file_ids: timepoint.treatFiles.map(String),
          treat_file_names: getFileNames(timepoint.treatFiles),
          control_file_ids_rna: [],
          control_file_names_rna: [],
          treat_file_ids_rna: [],
          treat_file_names_rna: [],
          group_name: timepoint.groupName || `Fraction ${index + 1}`,
        };
      } else {
        // Create empty timepoint structure for unused timepoints
        masterDict[timepointKey] = {
          control_file_ids: [],
          control_file_names: [],
          treat_file_ids: [],
          treat_file_names: [],
          control_file_ids_rna: [],
          control_file_names_rna: [],
          treat_file_ids_rna: [],
          treat_file_names_rna: [],
          group_name: `Fraction ${index + 1}`,
        };
      }
    }

    return masterDict;
  }, [timepoints, currentMaxVisibleTimepoint, allFiles]);

  // Load timepoint data from shared link (for PolyShift shortcode functionality)
  const loadTimepointData = useCallback((masterFileDict) => {
    if (!masterFileDict || typeof masterFileDict !== "object") {
      console.warn("Invalid master_file_dict provided to loadTimepointData");
      return;
    }

    const newTimepoints = [];
    let maxVisibleIndex = 0;

    // Parse the master_file_dict and recreate timepoints
    for (let index = 0; index <= 12; index++) {
      const timepointKey = `time_${index}`;
      const timepointData = masterFileDict[timepointKey];

      if (timepointData) {
        // Handle field name formats - prioritize old fields over RNA fields
        const controlFiles =
          timepointData.control_file_ids.map((id) => parseInt(id, 10)) ||
          timepointData.control_file_ids_rna.map((id) => parseInt(id, 10)) ||
          [];
        const treatFiles =
          timepointData.treat_file_ids.map((id) => parseInt(id, 10)) ||
          timepointData.treat_file_ids_rna.map((id) => parseInt(id, 10)) ||
          [];

        // If this timepoint has files, update the max visible index
        if (controlFiles.length > 0 || treatFiles.length > 0) {
          maxVisibleIndex = index;
        }

        newTimepoints[index] = {
          groupName: timepointData.group_name || `Fraction ${index + 1}`,
          controlFiles: controlFiles,
          treatFiles: treatFiles,
        };
      } else {
        // Create empty timepoint
        newTimepoints[index] = createEmptyTimepoint(index);
      }
    }

    // Ensure we have at least the first timepoint
    if (newTimepoints.length === 0) {
      newTimepoints[0] = createEmptyTimepoint(0);
    }

    setTimepoints(newTimepoints);
    setCurrentMaxVisibleTimepoint(Math.max(0, maxVisibleIndex));

    console.log("PolyShift timepoints loaded from shared link:", {
      timepoints: newTimepoints,
      maxVisible: maxVisibleIndex,
    });
  }, []);

  // ✅ Define getTimepointStats as a memoized VALUE (not a function)
  const getTimepointStats = useMemo(() => {
    const activeTimepoints = timepoints.slice(
      0,
      currentMaxVisibleTimepoint + 1
    );

    return {
      totalTimepoints: activeTimepoints.length,
      timepointsWithFiles: activeTimepoints.filter(
        (tp) => tp.controlFiles.length > 0 || tp.treatFiles.length > 0
      ).length,
      totalFiles: activeTimepoints.reduce(
        (sum, tp) => sum + tp.controlFiles.length + tp.treatFiles.length,
        0
      ),
      controlFiles: activeTimepoints.reduce(
        (sum, tp) => sum + tp.controlFiles.length,
        0
      ),
      treatFiles: activeTimepoints.reduce(
        (sum, tp) => sum + tp.treatFiles.length,
        0
      ),
    };
  }, [timepoints, currentMaxVisibleTimepoint]);

  // ✅ CRITICAL: Make sure to export getTimepointStats
  return {
    timepoints,
    currentMaxVisibleTimepoint,
    addTimepoint,
    removeTimepoint,
    updateTimepointName,
    addFilesToTimepoint,
    removeFilesFromTimepoint,
    clearTimepoint,
    clearAllTimepoints,
    validateTimepoints,
    getMasterFileDict,
    getTimepointStats, // ✅ Export as value, not function
    loadTimepointData, // ✅ Export loadTimepointData for shared link support
  };
};
